/**
Project: Voice Panel (c)
Title: Voice Route Info 
Description: Component to change the priority of the regions based on the policy status.
Copyrights: This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this source code package.
*/
import React, { Component } from "react";
import { connect } from "react-redux";
import { Text, Button, ChevronDownMediumIcon } from "@fluentui/react-northstar";
import { Grid, Row, Col } from "react-flexbox-grid";
import classNames from "classnames";
import IntlUtil from "../../../../core/helpers/intl-util";
import {
  incrementVoiceRoutePriority,
  decrementVoiceRoutePriority,
  assignVoiceRoute,
  unassignVoiceRoute,
  updateVoiceRoutePriority,
  enableVoiceRoutesUpdateButton,
  setVoiceRoutes,
} from "../actions/routing-actions";
import _ from "lodash";

class VoiceRouteInfo extends Component {
  _intl_ns_vpa_routing = "vpa_routing";

  constructor(props) {
    super(props);
    this.state = {
      routeIsHovered: false,
    };
  }

  handleButtonClick = async (type) => {
    const { route, voiceRoutes, enableVoiceRoutesUpdateButton } = this.props;
    let sortedRoutes = _.orderBy(voiceRoutes, "routePriority", ["asc"]);
    let routePriorities = sortedRoutes.map((r) => r.routePriority);
    let updatedRoutes = sortedRoutes;
    switch (type) {
      case "assign":
        let assignedRoutes = updatedRoutes.filter((r) => r.routeStatus === 1);
        assignedRoutes.push({ ...route, routeStatus: 1 });
        let unassignedRoutes = updatedRoutes.filter(
          (r) => r.routeStatus === 0 && r.routeName !== route.routeName
        );
        updatedRoutes = [...assignedRoutes, ...unassignedRoutes];
        break;
      case "unassign":
        let assignedTempRoutes = updatedRoutes.filter(
          (r) => r.routeStatus === 1 && r.routeName !== route.routeName
        );
        let unassignedTempRoutes = updatedRoutes.filter(
          (r) => r.routeStatus === 0
        );
        unassignedTempRoutes.push({ ...route, routeStatus: 0 });
        updatedRoutes = [...assignedTempRoutes, ...unassignedTempRoutes];
        break;
      case "increment":
        let incIndex = updatedRoutes.findIndex(
          (r) => r.routeName === route.routeName
        );
        let c = updatedRoutes[incIndex - 1];
        let d = updatedRoutes[incIndex];
        updatedRoutes[incIndex - 1] = d;
        updatedRoutes[incIndex] = c;
        break;
      case "decrement":
        let decIndex = updatedRoutes.findIndex(
          (r) => r.routeName === route.routeName
        );
        let a = updatedRoutes[decIndex];
        let b = updatedRoutes[decIndex + 1];
        updatedRoutes[decIndex] = b;
        updatedRoutes[decIndex + 1] = a;
        break;
      default:
        return;
    }
    updatedRoutes = updatedRoutes.map((r, index) => {
      r.routePriority = routePriorities[index];
      return r;
    });
    this.props.setVoiceRoutes(updatedRoutes);
    enableVoiceRoutesUpdateButton();
  };

  renderVoiceRouteButton(route, selectedRoutingPolicy, isHovered) {
    if (route.routeStatus === 0) {
      return (
        <Button
          text
          content={IntlUtil.getText(
            this._intl_ns_vpa_routing,
            "content.assign"
          )}
          primary
          onClick={() => this.handleButtonClick("assign")}
          disabled={selectedRoutingPolicy.policyStatus === 0}
        />
      );
    } else if (route.routeStatus === 1 && isHovered) {
      return (
        <Button
          text
          content={IntlUtil.getText(
            this._intl_ns_vpa_routing,
            "content.unassign"
          )}
          onClick={() => this.handleButtonClick("unassign")}
          disabled={
            route.routePriority === this.props.defaultRoutePriority ||
            selectedRoutingPolicy.policyStatus === 0
          }
        />
      );
    }
  }

  renderVoiceRouteArrows(route, selectedRoutingPolicy, isHovered) {
    return (
      isHovered && (
        <span className="vpa-routing-routes-arrow">
          <Button
            disabled={
              route.routePriority === this.props.defaultRoutePriority ||
              selectedRoutingPolicy.policyStatus === 0 ||
              route.routeStatus === 0
            }
            iconOnly
            onClick={() => this.handleButtonClick("increment")}
            text
            icon={<ChevronDownMediumIcon />}
            className="vpa-routing-routes-arrow-up"
          />
          <Button
            disabled={
              // route.routePriority === this.assignedVoiceRoutes.length ||
              selectedRoutingPolicy.policyStatus === 0 ||
              route.routeStatus === 0 ||
              this.props.index === this.props.voiceRoutes?.length - 1
            }
            iconOnly
            onClick={() => this.handleButtonClick("decrement")}
            text
            icon={<ChevronDownMediumIcon />}
            className="vpa-routing-routes-arrow-down"
          />
        </span>
      )
    );
  }

  render() {
    const { route, selectedRoutingPolicy } = this.props;
    const { routeIsHovered } = this.state;
    return (
      <Grid fluid className="p-0">
        <Row
          className="vpa-routing-routes-row-info"
          onMouseEnter={() => this.setState({ routeIsHovered: true })}
          onMouseLeave={() => this.setState({ routeIsHovered: false })}
        >
          <Col xs={2}>
            {this.renderVoiceRouteArrows(
              route,
              selectedRoutingPolicy,
              routeIsHovered
            )}
          </Col>
          <Col xs={7}>
            <Text
              disabled={
                route.routeStatus === 0 ||
                selectedRoutingPolicy.policyStatus === 0
              }
              className={classNames(
                "page-text",
                "vpa-routing-routes-col-text",
                {
                  "text-semibold":
                    route.routePriority === this.props.defaultRoutePriority,
                },
                {
                  "text-disable":
                    route.routeStatus === 0 ||
                    selectedRoutingPolicy.policyStatus === 0,
                }
              )}
            >
              {route.routeDescription}
            </Text>
          </Col>
          <Col xs={3} className="vpa-routing-routes-button">
            {this.renderVoiceRouteButton(
              route,
              selectedRoutingPolicy,
              routeIsHovered
            )}
          </Col>
        </Row>
      </Grid>
    );
  }
}

const mapStateToProps = (state) => ({});

const mapActionToProps = {
  incrementVoiceRoutePriority,
  decrementVoiceRoutePriority,
  assignVoiceRoute,
  unassignVoiceRoute,
  updateVoiceRoutePriority,
  enableVoiceRoutesUpdateButton,
  setVoiceRoutes,
};

export default connect(mapStateToProps, mapActionToProps)(VoiceRouteInfo);
